import React from "react";
import { PricingPlan } from "@/api/models/api-types";
import { Check, Rocket, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCurrencySymbol } from "@/lib/currency";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/hooks/use-theme";

interface PricingCardDesktopProps {
  plan: PricingPlan;
  currentPlanId?: string | null;
  isCurrentPlanExpired?: boolean;
  onSubscribe: (plan: PricingPlan) => void;
  isLoading?: boolean;
}

// Gradient Background Component
const GradientBackground: React.FC<{ isPopular: boolean }> = ({
  isPopular,
}) => {
  const { mode } = useTheme();

  if (!isPopular) return null;

  // Light mode gradients
  const lightGradients = [
    `url('data:image/svg+xml;utf8,<svg viewBox="0 0 283 438" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"><rect x="0" y="0" height="100%" width="100%" fill="url(%23grad)" opacity="0.7"/><defs><radialGradient id="grad" gradientUnits="userSpaceOnUse" cx="0" cy="0" r="10" gradientTransform="matrix(-8.0589 -14.038 38.152 8.7696 74.03 438)"><stop stop-color="rgba(240,232,255,1)" offset="0"/><stop stop-color="rgba(255,255,255,0)" offset="1"/></radialGradient></defs></svg>')`,
    `url('data:image/svg+xml;utf8,<svg viewBox="0 0 283 438" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"><rect x="0" y="0" height="100%" width="100%" fill="url(%23grad)" opacity="0.7"/><defs><radialGradient id="grad" gradientUnits="userSpaceOnUse" cx="0" cy="0" r="10" gradientTransform="matrix(-3.5141 -18.156 20.695 -33.273 207.56 428.17)"><stop stop-color="rgba(226,237,255,1)" offset="0"/><stop stop-color="rgba(255,255,255,0)" offset="1"/></radialGradient></defs></svg>')`,
  ];

  // Dark mode gradients - daha koyu ve uyumlu renkler
  const darkGradients = [
    `url('data:image/svg+xml;utf8,<svg viewBox="0 0 283 438" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"><rect x="0" y="0" height="100%" width="100%" fill="url(%23grad)" opacity="0.6"/><defs><radialGradient id="grad" gradientUnits="userSpaceOnUse" cx="0" cy="0" r="10" gradientTransform="matrix(-8.0589 -14.038 38.152 8.7696 74.03 438)"><stop stop-color="rgba(30,58,138,0.8)" offset="0"/><stop stop-color="rgba(30,58,138,0)" offset="1"/></radialGradient></defs></svg>')`,
    `url('data:image/svg+xml;utf8,<svg viewBox="0 0 283 438" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"><rect x="0" y="0" height="100%" width="100%" fill="url(%23grad)" opacity="0.6"/><defs><radialGradient id="grad" gradientUnits="userSpaceOnUse" cx="0" cy="0" r="10" gradientTransform="matrix(-3.5141 -18.156 20.695 -33.273 207.56 428.17)"><stop stop-color="rgba(59,130,246,0.6)" offset="0"/><stop stop-color="rgba(59,130,246,0)" offset="1"/></radialGradient></defs></svg>')`,
  ];

  const gradients = mode === "dark" ? darkGradients : lightGradients;

  return (
    <div
      className="absolute inset-0 rounded-xl pointer-events-none"
      style={{
        backgroundImage: gradients.join(", "),
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    />
  );
};

export const PricingCardDesktop: React.FC<PricingCardDesktopProps> = ({
  plan,
  currentPlanId,
  isCurrentPlanExpired = false,
  onSubscribe,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();

  const totalCredits = plan.tokenAmount + (plan.bonusCredits || 0);

  const currencySymbol = getCurrencySymbol(plan.currency);

  const handleSubscribe = () => {
    onSubscribe(plan);
  };

  // Check if this is the user's current plan
  const isCurrentPlan = currentPlanId === plan._id && !isCurrentPlanExpired;

  // Default features list
  const defaultFeatures = [
    {
      text:
        plan.planType === "recurring"
          ? t("pricing.defaultFeatures.creditsPerMonth", {
              amount: plan.tokenAmount.toLocaleString(),
            })
          : t("pricing.defaultFeatures.totalCredits", {
              amount: totalCredits.toLocaleString(),
            }),
    },
    { text: t("pricing.defaultFeatures.privateGeneration") },
    { text: t("pricing.defaultFeatures.allFeaturesAvailable") },
    { text: t("pricing.defaultFeatures.highQualityVideo") },
    { text: t("pricing.defaultFeatures.commercialLicense") },
    { text: t("pricing.defaultFeatures.cloudStorage") },
  ];

  // Determine button configuration
  const getButtonConfig = () => {
    // Free plan'lar için buton gösterme
    if (
      plan.discountedPrice === 0 ||
      plan.name.toLowerCase().includes("free")
    ) {
      return null;
    }

    if (isCurrentPlan) {
      return {
        text: t("pricing.currentPlan"),
        variant: "outline" as const,
        disabled: true,
        icon: null,
      };
    }

    if (plan.name.toLowerCase().includes("plus")) {
      return {
        text: t("pricing.upgradeToPlus"),
        variant: "darkPrimary" as const,
        disabled: false,
        icon: <Rocket className="w-4 h-4" />,
      };
    }

    if (plan.name.toLowerCase().includes("pro")) {
      return {
        text: t("pricing.upgradeToPro"),
        variant: "outline" as const,
        disabled: false,
        icon: null,
      };
    }

    return {
      text: t("pricing.buyNow"),
      variant: "outline" as const,
      disabled: false,
      icon: null,
    };
  };

  const buttonConfig = getButtonConfig();

  return (
    <div
      className={cn(
        "relative p-5 rounded-2xl border transition-all duration-200 h-full flex flex-col",
        plan.isPopular
          ? "border-[#a4bcfd] dark:border-blue-400"
          : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700",
      )}
    >
      {/* Gradient Background */}
      <GradientBackground isPopular={plan.isPopular} />
      {/* Popular Badge */}
      {plan.isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-blue-50 dark:bg-blue-900/50 border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 text-sm font-medium px-3 py-1 rounded-md">
            {t("pricing.mostPopular")}
          </span>
        </div>
      )}

      {/* Plan Name */}
      <div className="mb-4">
        <h3
          className={cn(
            "text-xl font-semibold mb-1",
            plan.isPopular
              ? "text-blue-600 dark:text-blue-400"
              : "text-zinc-900 dark:text-zinc-100",
          )}
        >
          {plan.name}
        </h3>
        <p className="text-xs text-zinc-600 dark:text-zinc-400">
          {totalCredits.toLocaleString()} {t("pricing.monthlyCredits")}
        </p>
      </div>

      {/* Offer Badge — backend'den gelirse kullan, yoksa starter plan için fallback */}
      {(plan.offerBadge || plan.name.toLowerCase().includes("starter")) && (
        <div className="mb-5 rounded-lg overflow-hidden bg-gradient-to-r from-orange-500 to-red-500 p-3 shadow-sm">
          <div className="flex items-start gap-2">
            <Zap className="w-3.5 h-3.5 text-white flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] font-bold text-white uppercase tracking-wide leading-tight">
                {plan.offerBadge?.title ?? t("pricing.starterBadgeTitle")}
              </p>
              <p className="text-[11px] text-orange-100 mt-0.5 leading-tight">
                {plan.offerBadge?.description ?? t("pricing.starterBadgeDescription")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Price */}
      <div className="mb-8">
        <div className="flex items-baseline gap-2">
          {/* Original Price (if discount exists) */}
          {plan.originalPrice > plan.discountedPrice && (
            <span className="text-lg font-medium text-gray-400 dark:text-gray-500 line-through">
              {currencySymbol}{plan.originalPrice}
            </span>
          )}
          {/* Discounted Price */}
          <span className="text-3xl font-semibold text-zinc-900 dark:text-zinc-100">
            {currencySymbol}{plan.discountedPrice}
          </span>
          <span className="text-xs text-zinc-600 dark:text-zinc-400">
            {t("pricing.perMonth")}
          </span>
        </div>
        {/* USD equivalent — only when local currency is not USD */}
        {plan.usdPrices &&
          plan.currency?.toUpperCase() !== plan.usdPrices.currency?.toUpperCase() && (
            <p className="mt-1 text-xs text-zinc-400 dark:text-zinc-500">
              {t("pricing.usdEquivalent", {
                price: plan.usdPrices.discountedPrice,
              })}
            </p>
          )}
      </div>

      {/* Action Button - Only show for paid plans */}
      <div className="mb-8">
        {buttonConfig ? (
          <Button
            variant={plan.isPopular ? "darkPrimary" : buttonConfig.variant}
            size="md"
            onClick={handleSubscribe}
            disabled={buttonConfig.disabled || isLoading}
            className={cn(
              "w-full rounded-xl transition-all duration-300 active:scale-[0.985] hover:-translate-y-0.5",
              !plan.isPopular &&
                "text-zinc-900 dark:text-zinc-100 bg-white/90 dark:bg-zinc-900/80 border-zinc-200 dark:border-zinc-700 shadow-[0_8px_18px_rgba(24,24,27,0.08)] hover:shadow-[0_12px_22px_rgba(24,24,27,0.12)]",
              plan.isPopular && "text-white",
              plan.isPopular &&
                "shadow-[0_12px_24px_rgba(24,24,27,0.38)] hover:shadow-[0_16px_30px_rgba(24,24,27,0.48)]",
            )}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                {t("pricing.processing")}
              </>
            ) : (
              <>
                {plan.isPopular ? (
                  <Rocket className="w-4 h-4" />
                ) : (
                  buttonConfig.icon
                )}
                {buttonConfig.text}
              </>
            )}
          </Button>
        ) : (
          // Dummy div to maintain spacing for free plans
          <div className="h-10 w-full" />
        )}
      </div>

      {/* Features */}
      <ul className="space-y-4 flex-grow">
        {(plan.features ?? []).length > 0
          ? (plan.features ?? []).map((feature) => (
              <li key={feature._id} className="flex items-center gap-2.5">
                <span className="w-4 h-4 bg-blue-300/10  rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-blue-500   flex-shrink-0" />
                </span>

                <span className="text-sm text-zinc-600 dark:text-zinc-400">
                  {feature.text[(i18n.resolvedLanguage || i18n.language) as keyof typeof feature.text] ||
                    feature.text.en}
                </span>
              </li>
            ))
          : defaultFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-2.5">
                <span className="w-4 h-4 bg-blue-300/10  rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-blue-500  flex-shrink-0" />
                </span>
                <span className="text-sm text-zinc-600 dark:text-zinc-400">
                  {feature.text}
                </span>
              </li>
            ))}
      </ul>
    </div>
  );
};
